/**
 * Admin API Client (PR A6)
 * Lightweight fetch wrapper for Admin API calls
 */

// Use consistent BACKEND_URL environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

/**
 * Get JWT token from localStorage
 */
const getToken = () => {
  return localStorage.getItem('token');
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = () => {
  const token = getToken();
  if (!token) return false;

  try {
    // Decode JWT payload (base64)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check expiration
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    
    return now < exp;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

/**
 * Get user info from JWT token or localStorage
 */
export const getUserFromToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    // First try to get user from localStorage (set during login)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      return {
        id: userData.id,
        actorId: userData.id,
        impersonatorId: null,
        isImpersonating: false,
        role: userData.role || null,
        role_ids: Array.isArray(userData.role_ids) ? userData.role_ids : null,
        roles: Array.isArray(userData.roles) ? userData.roles : null,
        email: userData.email || null,
        full_name: userData.full_name || null
      };
    }
    
    // Fallback to parsing token (may not have all fields)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.sub || payload.actor_id,
      actorId: payload.actor_id,
      impersonatorId: payload.impersonator_id,
      isImpersonating: !!payload.impersonator_id,
      role: payload.role || null,
      email: payload.email || null
    };
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
};

/**
 * Make authenticated API request
 * 
 * @param {string} endpoint - API endpoint (e.g., '/api/admin/users')
 * @param {object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  
  if (!token) {
    // Redirect to login if no token
    window.location.replace('/login');
    throw new Error('No authentication token');
  }

  const url = `${API_BASE}${endpoint}`;
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, config);

    // Feb 25, 2026 — short-circuit any 401/403 surfaced during a
    // logout-in-progress. Polling components fire requests that race
    // the navigation; their errors would otherwise flash as "Permission
    // denied" / "Authentication failed" toasts on the way to /login.
    if (response.status === 401 || response.status === 403) {
      try {
        if (window.__titleflowIsLoggingOut__) {
          // Throw a benign, identifiable error. Component handlers may
          // log it but the suppressor has already disabled toast.error,
          // so the user sees nothing. Navigation to /login is already
          // in flight from App.logout().
          const e = new Error('Logout in progress');
          e.silent = true;
          throw e;
        }
      } catch (probeErr) {
        if (probeErr.silent) throw probeErr;
        /* probing window failed — fall through to normal handling */
      }
    }

    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401) {
      // Check for idle timeout specific header/message
      const logoutReason = response.headers.get('X-Logout-Reason');
      const errorData = await response.json().catch(() => ({ detail: 'Authentication failed' }));
      
      const isIdleTimeout = logoutReason === 'idle_timeout' || 
                           errorData.detail?.includes('inactivity');
      
      if (isIdleTimeout) {
        // Let IdleManager handle idle timeouts — it shows a warning modal first.
        // Do NOT redirect immediately; dispatch a custom event instead.
        console.warn('[adminApi] Idle timeout 401 — deferring to IdleManager. URL:', endpoint);
        window.dispatchEvent(new CustomEvent('idle-timeout-401', { detail: { source: 'adminApi', url: endpoint } }));
        throw new Error('Session expired due to inactivity');
      }
      
      // Feb 27, 2026: role-change forced logout. Clear all client-side
      // role/permission caches so the next session boots clean.
      const isRoleChanged = logoutReason === 'role_changed' ||
                            errorData.detail?.toLowerCase?.().includes('role was updated');
      if (isRoleChanged) {
        console.warn('[adminApi] Role changed — forcing re-login with cache clear');
        try {
          const { clearPermissionsCache } = require('../hooks/usePermissions');
          clearPermissionsCache?.();
        } catch (_) { /* hook not loaded yet */ }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refresh_token');
        // Feb 2026 unified logout notice. Replaces the old `toast.info`
        // which was wiped by the subsequent window.location.replace.
        try {
          const { queueLogoutNotice } = require('./logoutNotice');
          queueLogoutNotice('role_changed');
        } catch (_) { /* helper not loaded */ }
        window.location.replace('/login');
        throw new Error('Role was updated — please log in again');
      }
      
      // Non-idle 401 (bad token, password changed, etc.) — redirect immediately
      console.warn('Authentication failed, redirecting to login');
      localStorage.removeItem('token');
      // Queue a unified logout notice so the Login page surfaces a
      // toast. Pick the best-guess scenario from the X-Logout-Reason
      // header / detail; fall back to session_invalid.
      try {
        const { queueLogoutNotice } = require('./logoutNotice');
        const scenario =
          logoutReason === 'token_expired' ? 'token_expired' :
          logoutReason === 'token_revoked' ? 'token_revoked' :
          logoutReason === 'password_changed' ? 'password_changed' :
          errorData.detail?.toLowerCase?.().includes('password has been changed') ? 'password_changed' :
          errorData.detail?.toLowerCase?.().includes('expired') ? 'token_expired' :
          'session_invalid';
        queueLogoutNotice(scenario);
      } catch (_) { /* helper not loaded */ }
      window.location.replace('/login');
      throw new Error('Authentication failed');
    }

    // Handle 403 Forbidden - permission denied
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({ detail: 'Permission denied' }));
      throw new Error(errorData.detail || 'Permission denied');
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      const error = new Error(errorData.detail || `Request failed with status ${response.status}`);
      error.status = response.status;  // Preserve HTTP status code
      error.statusText = response.statusText;
      throw error;
    }

    // Return response data
    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

/**
 * Check if user has specific permission
 * Note: This is a client-side check only for UI purposes.
 * Server always enforces permissions.
 * 
 * @param {string} resource - Resource name (e.g., 'users', 'roles')
 * @param {string} action - Action name (e.g., 'view', 'create')
 * @returns {Promise<boolean>} True if user has permission
 */
export const hasPermission = async (resource, action) => {
  // Call a lightweight endpoint to check permission
  // For now, we'll infer from available permissions
  // In a real app, you might have a /api/auth/permissions endpoint
  
  // TODO: Implement permission check
  // For now, return true to allow access (server will enforce)
  return true;
};

/**
 * Stop impersonation session
 */
export const stopImpersonation = async () => {
  try {
    await apiRequest('/api/admin/impersonation/stop', {
      method: 'POST'
    });
    
    // Reload page to get new token
    window.location.reload();
  } catch (error) {
    console.error('Error stopping impersonation:', error);
    throw error;
  }
};

export default {
  isAuthenticated,
  getUserFromToken,
  apiRequest,
  hasPermission,
  stopImpersonation
};
